import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Progress } from 'antd';
import { TrophyOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SoundOutlined, CalculatorOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const MathQuiz = ({ onGameComplete }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [showStartScreen, setShowStartScreen] = useState(true);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [timeLeft, setTimeLeft] = useState(30);
    const [answered, setAnswered] = useState(false);
    const [isReading, setIsReading] = useState(false);
    const [difficulty, setDifficulty] = useState('medium'); // easy, medium, hard
    const speechSynthesisRef = useRef(null);
    const clockTickAudioRef = useRef(null);
    const correctAudioRef = useRef(null);
    const failAudioRef = useRef(null);

    // Generate math questions dynamically
    const generateQuestion = () => {
        const operations = ['+', '-', '×', '÷'];
        const operation = operations[Math.floor(Math.random() * operations.length)];
        let num1, num2, answer, options;
        
        // Adjust difficulty based on current question index
        let currentDifficulty = difficulty;
        if (currentQuestionIndex < 8) {
            currentDifficulty = 'easy';
        } else if (currentQuestionIndex < 17) {
            currentDifficulty = 'medium';
        } else {
            currentDifficulty = 'hard';
        }

        switch (operation) {
            case '+':
                if (currentDifficulty === 'easy') {
                    num1 = Math.floor(Math.random() * 50) + 1;
                    num2 = Math.floor(Math.random() * 50) + 1;
                } else if (currentDifficulty === 'medium') {
                    num1 = Math.floor(Math.random() * 200) + 50;
                    num2 = Math.floor(Math.random() * 200) + 50;
                } else {
                    num1 = Math.floor(Math.random() * 500) + 200;
                    num2 = Math.floor(Math.random() * 500) + 200;
                }
                answer = num1 + num2;
                break;
            
            case '-':
                if (currentDifficulty === 'easy') {
                    num1 = Math.floor(Math.random() * 50) + 20;
                    num2 = Math.floor(Math.random() * num1) + 1;
                } else if (currentDifficulty === 'medium') {
                    num1 = Math.floor(Math.random() * 300) + 100;
                    num2 = Math.floor(Math.random() * num1) + 1;
                } else {
                    num1 = Math.floor(Math.random() * 1000) + 500;
                    num2 = Math.floor(Math.random() * num1) + 1;
                }
                answer = num1 - num2;
                break;
            
            case '×':
                if (currentDifficulty === 'easy') {
                    num1 = Math.floor(Math.random() * 10) + 1;
                    num2 = Math.floor(Math.random() * 10) + 1;
                } else if (currentDifficulty === 'medium') {
                    num1 = Math.floor(Math.random() * 20) + 5;
                    num2 = Math.floor(Math.random() * 20) + 5;
                } else {
                    num1 = Math.floor(Math.random() * 50) + 10;
                    num2 = Math.floor(Math.random() * 50) + 10;
                }
                answer = num1 * num2;
                break;
            
            case '÷':
                if (currentDifficulty === 'easy') {
                    num2 = Math.floor(Math.random() * 10) + 2;
                    const multiplier = Math.floor(Math.random() * 10) + 1;
                    num1 = num2 * multiplier;
                } else if (currentDifficulty === 'medium') {
                    num2 = Math.floor(Math.random() * 20) + 2;
                    const multiplier = Math.floor(Math.random() * 20) + 1;
                    num1 = num2 * multiplier;
                } else {
                    num2 = Math.floor(Math.random() * 50) + 2;
                    const multiplier = Math.floor(Math.random() * 50) + 1;
                    num1 = num2 * multiplier;
                }
                answer = num1 / num2;
                break;
        }

        // Generate wrong options
        const wrongOptions = [];
        while (wrongOptions.length < 3) {
            let wrongAnswer;
            if (operation === '÷') {
                // For division, generate close wrong answers
                wrongAnswer = answer + (Math.random() > 0.5 ? 1 : -1) * (Math.floor(Math.random() * 5) + 1);
            } else {
                // For other operations, generate answers within a range
                const variance = Math.max(10, Math.abs(answer) * 0.2);
                wrongAnswer = answer + Math.floor(Math.random() * variance * 2) - variance;
            }
            
            // Make sure wrong answer is different from correct answer and other wrong answers
            if (wrongAnswer !== answer && !wrongOptions.includes(wrongAnswer) && wrongAnswer > 0) {
                wrongOptions.push(wrongAnswer);
            }
        }

        // Shuffle options
        const allOptions = [answer, ...wrongOptions];
        for (let i = allOptions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
        }

        const correctIndex = allOptions.indexOf(answer);

        return {
            question: `${num1} ${operation} ${num2} = ?`,
            num1,
            num2,
            operation,
            options: allOptions.map(opt => Math.round(opt * 100) / 100), // Round to 2 decimal places
            correct: correctIndex,
            answer: Math.round(answer * 100) / 100
        };
    };

    const startGame = () => {
        // Stop any ongoing speech
        stopReading();
        
        setShowStartScreen(false);
        setGameStarted(true);
        setGameEnded(false);
        setScore(0);
        setCurrentQuestionIndex(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setTimeLeft(30);
        setAnswered(false);
        setIsReading(false);
        setCurrentQuestion(generateQuestion());
        
        // Start background clock-tick sound
        if (clockTickAudioRef.current) {
            try {
                clockTickAudioRef.current.currentTime = 0; // Reset to start
                clockTickAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play clock-tick sound:', error);
                });
            } catch (error) {
                console.log('Error playing clock-tick sound:', error);
            }
        }
    };

    useEffect(() => {
        if (!gameStarted || gameEnded || !currentQuestion) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    handleTimeUp();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [gameStarted, gameEnded, currentQuestion]);

    const handleTimeUp = () => {
        // Stop any ongoing speech
        stopReading();
        
        setAnswered(true);
        setShowResult(true);
        
        // Play fail sound when time runs out
        if (failAudioRef.current) {
            try {
                failAudioRef.current.currentTime = 0; // Reset to start
                failAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play fail sound:', error);
                });
            } catch (error) {
                console.log('Error playing fail sound:', error);
            }
        }
        
        setTimeout(() => {
            nextQuestion();
        }, 2000);
    };

    const handleAnswer = (answerIndex) => {
        if (answered) return;
        
        // Stop any ongoing speech
        stopReading();
        
        setSelectedAnswer(answerIndex);
        setAnswered(true);
        
        const isCorrect = answerIndex === currentQuestion.correct;
        if (isCorrect) {
            setScore(prev => prev + 10);
            
            // Play correct sound
            if (correctAudioRef.current) {
                try {
                    correctAudioRef.current.currentTime = 0; // Reset to start
                    correctAudioRef.current.play().catch(error => {
                        // Handle autoplay restrictions
                        console.log('Could not play correct sound:', error);
                    });
                } catch (error) {
                    console.log('Error playing correct sound:', error);
                }
            }
        } else {
            // Play fail sound
            if (failAudioRef.current) {
                try {
                    failAudioRef.current.currentTime = 0; // Reset to start
                    failAudioRef.current.play().catch(error => {
                        // Handle autoplay restrictions
                        console.log('Could not play fail sound:', error);
                    });
                } catch (error) {
                    console.log('Error playing fail sound:', error);
                }
            }
        }
        
        setShowResult(true);
        
        setTimeout(() => {
            nextQuestion();
        }, 2000);
    };

    const nextQuestion = () => {
        // Stop any ongoing speech
        stopReading();
        
        if (currentQuestionIndex >= 24) {
            // Game over after 25 questions
            setGameEnded(true);
            setGameStarted(false);
            
            // Stop background clock-tick sound
            if (clockTickAudioRef.current) {
                clockTickAudioRef.current.pause();
                clockTickAudioRef.current.currentTime = 0;
            }
            
            if (onGameComplete) {
                setTimeout(() => onGameComplete(score), 500);
            }
        } else {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setShowResult(false);
            setTimeLeft(30);
            setAnswered(false);
            setCurrentQuestion(generateQuestion());
        }
    };

    const readQuestion = () => {
        if (!currentQuestion) return;
        
        // Stop any ongoing speech
        if (speechSynthesisRef.current) {
            window.speechSynthesis.cancel();
        }
        
        // Check if browser supports speech synthesis
        if ('speechSynthesis' in window) {
            const voices = window.speechSynthesis.getVoices();
            
            let selectedVoice = null;
            
            const preferredVoices = [
                'Google UK English Female',
                'Microsoft Zira - English (United States)',
                'Microsoft Hazel - English (Great Britain)',
                'Samantha',
                'Karen',
                'Victoria',
                'Alex',
                'Siri'
            ];
            
            for (const preferredName of preferredVoices) {
                const voice = voices.find(v => v.name.includes(preferredName) || v.name === preferredName);
                if (voice) {
                    selectedVoice = voice;
                    break;
                }
            }
            
            if (!selectedVoice) {
                selectedVoice = voices.find(v => 
                    v.lang.startsWith('en') && 
                    (v.name.toLowerCase().includes('female') || 
                     v.name.toLowerCase().includes('zira') ||
                     v.name.toLowerCase().includes('hazel') ||
                     v.name.toLowerCase().includes('samantha'))
                );
            }
            
            if (!selectedVoice) {
                selectedVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
            }
            
            // Convert operation symbol to word for better speech
            let questionText = currentQuestion.question;
            questionText = questionText.replace('×', 'times');
            questionText = questionText.replace('÷', 'divided by');
            questionText = questionText.replace('+', 'plus');
            questionText = questionText.replace('-', 'minus');
            questionText = questionText.replace(' = ?', ' equals what?');
            
            const utterance = new SpeechSynthesisUtterance(questionText);
            utterance.lang = 'en-US';
            utterance.rate = 0.9;
            utterance.pitch = 1.0;
            utterance.volume = 1;
            
            if (selectedVoice) {
                utterance.voice = selectedVoice;
            }
            
            utterance.onstart = () => {
                setIsReading(true);
            };
            
            utterance.onend = () => {
                setIsReading(false);
                speechSynthesisRef.current = null;
            };
            
            utterance.onerror = () => {
                setIsReading(false);
                speechSynthesisRef.current = null;
            };
            
            speechSynthesisRef.current = utterance;
            
            if (voices.length === 0) {
                window.speechSynthesis.onvoiceschanged = () => {
                    const updatedVoices = window.speechSynthesis.getVoices();
                    if (updatedVoices.length > 0 && selectedVoice) {
                        const voice = updatedVoices.find(v => v.name === selectedVoice.name);
                        if (voice) utterance.voice = voice;
                    }
                    window.speechSynthesis.speak(utterance);
                };
            } else {
                window.speechSynthesis.speak(utterance);
            }
        } else {
            alert('Text-to-speech is not supported in your browser.');
        }
    };

    const stopReading = () => {
        if (speechSynthesisRef.current) {
            window.speechSynthesis.cancel();
            speechSynthesisRef.current = null;
            setIsReading(false);
        }
    };

    const readText = (text, callback) => {
        // Stop any ongoing speech
        if (speechSynthesisRef.current) {
            window.speechSynthesis.cancel();
        }
        
        if ('speechSynthesis' in window) {
            const voices = window.speechSynthesis.getVoices();
            
            let selectedVoice = null;
            
            const preferredVoices = [
                'Google UK English Female',
                'Microsoft Zira - English (United States)',
                'Microsoft Hazel - English (Great Britain)',
                'Samantha',
                'Karen',
                'Victoria',
                'Alex',
                'Siri'
            ];
            
            for (const preferredName of preferredVoices) {
                const voice = voices.find(v => v.name.includes(preferredName) || v.name === preferredName);
                if (voice) {
                    selectedVoice = voice;
                    break;
                }
            }
            
            if (!selectedVoice) {
                selectedVoice = voices.find(v => 
                    v.lang.startsWith('en') && 
                    (v.name.toLowerCase().includes('female') || 
                     v.name.toLowerCase().includes('zira') ||
                     v.name.toLowerCase().includes('hazel') ||
                     v.name.toLowerCase().includes('samantha'))
                );
            }
            
            if (!selectedVoice) {
                selectedVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
            }
            
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'en-US';
            utterance.rate = 0.9;
            utterance.pitch = 1.0;
            utterance.volume = 1;
            
            if (selectedVoice) {
                utterance.voice = selectedVoice;
            }
            
            utterance.onstart = () => {
                if (callback) callback(true);
            };
            
            utterance.onend = () => {
                if (callback) callback(false);
                speechSynthesisRef.current = null;
            };
            
            utterance.onerror = () => {
                if (callback) callback(false);
                speechSynthesisRef.current = null;
            };
            
            speechSynthesisRef.current = utterance;
            
            if (voices.length === 0) {
                window.speechSynthesis.onvoiceschanged = () => {
                    const updatedVoices = window.speechSynthesis.getVoices();
                    if (updatedVoices.length > 0 && selectedVoice) {
                        const voice = updatedVoices.find(v => v.name === selectedVoice.name);
                        if (voice) utterance.voice = voice;
                    }
                    window.speechSynthesis.speak(utterance);
                };
            } else {
                window.speechSynthesis.speak(utterance);
            }
        } else {
            alert('Text-to-speech is not supported in your browser.');
        }
    };

    const readAllOptions = () => {
        if (!currentQuestion) return;
        
        let questionText = currentQuestion.question;
        questionText = questionText.replace('×', 'times');
        questionText = questionText.replace('÷', 'divided by');
        questionText = questionText.replace('+', 'plus');
        questionText = questionText.replace('-', 'minus');
        questionText = questionText.replace(' = ?', ' equals what?');
        
        const optionsText = currentQuestion.options.map((opt, idx) => {
            const optionLabel = String.fromCharCode(65 + idx); // A, B, C, D
            return `${optionLabel}. ${opt}`;
        }).join('. ');
        
        const fullText = `${questionText}. Options: ${optionsText}`;
        readText(fullText, setIsReading);
    };

    const readOption = (optionValue, optionIndex) => {
        const optionLabel = String.fromCharCode(65 + optionIndex); // A, B, C, D
        const text = `${optionLabel}. ${optionValue}`;
        readText(text, null);
    };

    // Initialize audio files
    useEffect(() => {
        clockTickAudioRef.current = new Audio('/sounds/clock-tick.mp3');
        clockTickAudioRef.current.volume = 0.3; // Set volume to 30% for background sound
        clockTickAudioRef.current.loop = true; // Loop the clock-tick sound
        
        correctAudioRef.current = new Audio('/sounds/correct.mp3');
        correctAudioRef.current.volume = 0.7; // Set volume to 70%
        
        failAudioRef.current = new Audio('/sounds/fail.mp3');
        failAudioRef.current.volume = 0.7; // Set volume to 70%
        
        return () => {
            if (clockTickAudioRef.current) {
                clockTickAudioRef.current.pause();
                clockTickAudioRef.current = null;
            }
            if (correctAudioRef.current) {
                correctAudioRef.current.pause();
                correctAudioRef.current = null;
            }
            if (failAudioRef.current) {
                failAudioRef.current.pause();
                failAudioRef.current = null;
            }
        };
    }, []);

    // Load voices when component mounts
    useEffect(() => {
        if ('speechSynthesis' in window) {
            const loadVoices = () => {
                window.speechSynthesis.getVoices();
            };
            loadVoices();
            window.speechSynthesis.onvoiceschanged = loadVoices;
            
            return () => {
                window.speechSynthesis.onvoiceschanged = null;
            };
        }
    }, []);

    // Cleanup speech when component unmounts or game ends
    useEffect(() => {
        return () => {
            if (speechSynthesisRef.current) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    const getScoreMessage = () => {
        if (score >= 225) return "🏆 Outstanding! You're a math genius!";
        if (score >= 175) return "⭐ Excellent! Great math skills!";
        if (score >= 125) return "👍 Good! Keep practicing!";
        if (score >= 75) return "📚 Not bad! Practice more!";
        return "💪 Keep practicing!";
    };

    const getDifficultyColor = () => {
        if (currentQuestionIndex < 8) return '#52c41a'; // Green for easy
        if (currentQuestionIndex < 17) return '#1890ff'; // Blue for medium
        return '#ff4d4f'; // Red for hard
    };

    const getDifficultyText = () => {
        if (currentQuestionIndex < 8) return 'Easy';
        if (currentQuestionIndex < 17) return 'Medium';
        return 'Hard';
    };

    // Splash Screen with Image
    if (showStartScreen && !gameStarted && !gameEnded) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '0',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                overflow: 'hidden'
            }}>
                {/* Game Preview Image */}
                <div style={{
                    width: '100%',
                    height: '400px',
                    background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 30%, #98D8C8 70%, #90EE90 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img 
                        src="/assets/images/math-challenge.jpg" 
                        alt="Math Challenge Game"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                        }}
                        onError={(e) => {
                            // Fallback if image doesn't exist
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
                
                {/* Start Button Section */}
                <div style={{
                    padding: '20px 16px',
                    background: 'rgba(255, 255, 255, 0.95)'
                }}>
                    <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '16px' }}>
                        🧮 Math Quiz Challenge
                    </Title>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px'
                    }}>
                        Test your math skills! Solve 25 problems with addition, subtraction, multiplication, and division.
                    </Text>
                    <Button
                        type="primary"
                        size="middle"
                        icon={<TrophyOutlined />}
                        onClick={startGame}
                        style={{
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 20px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Start Quiz
                    </Button>
                </div>
            </div>
        );
    }

    if (!gameStarted && !gameEnded) {
        return null;
    }

    if (gameEnded) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '30px 20px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <TrophyOutlined style={{ fontSize: '50px', color: '#ffd700', marginBottom: '15px' }} />
                <Title level={4} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '20px' }}>
                    Quiz Complete!
                </Title>
                <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a', display: 'block', marginBottom: '10px' }}>
                    Score: {score}/250
                </Text>
                <Text style={{ fontSize: '16px', color: '#666', display: 'block', marginBottom: '20px' }}>
                    {getScoreMessage()}
                </Text>
                <Button
                    type="primary"
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        setShowStartScreen(true);
                        setGameEnded(false);
                    }}
                    style={{
                        height: '44px',
                        fontSize: '15px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        padding: '0 24px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                    }}
                >
                    Play Again
                </Button>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            {/* Progress Bar */}
            <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                    <Text style={{ fontSize: '14px', fontWeight: 600, color: '#1890ff' }}>
                        Question {currentQuestionIndex + 1} of 25
                    </Text>
                    <Text style={{ fontSize: '14px', fontWeight: 600, color: getDifficultyColor() }}>
                        📊 {getDifficultyText()}
                    </Text>
                    <Text style={{ fontSize: '14px', fontWeight: 600, color: timeLeft <= 10 ? '#ff4d4f' : '#52c41a' }}>
                        ⏱️ {timeLeft}s
                    </Text>
                    <Text style={{ fontSize: '14px', fontWeight: 600, color: '#1890ff' }}>
                        Score: {score}
                    </Text>
                </div>
                <Progress 
                    percent={((currentQuestionIndex + 1) / 25) * 100} 
                    showInfo={false}
                    strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                    }}
                />
            </div>

            {/* Question Card */}
            {currentQuestion && (
                <div style={{
                    padding: '24px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(24, 144, 255, 0.2)',
                    marginBottom: '16px'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '12px',
                        marginBottom: '20px'
                    }}>
                        <Title level={4} style={{ margin: 0, color: '#1890ff', fontSize: '24px', textAlign: 'center', flex: 1, fontFamily: 'monospace' }}>
                            {currentQuestion.question}
                        </Title>
                        <Button
                            type={isReading ? "default" : "primary"}
                            shape="circle"
                            icon={<SoundOutlined />}
                            onClick={isReading ? stopReading : readAllOptions}
                            disabled={answered}
                            style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: answered
                                    ? 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)'
                                    : isReading 
                                    ? 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)'
                                    : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                border: 'none',
                                boxShadow: answered ? 'none' : '0 2px 8px rgba(24, 144, 255, 0.3)',
                                animation: isReading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                cursor: answered ? 'not-allowed' : 'pointer',
                                opacity: answered ? 0.6 : 1
                            }}
                            title={answered ? 'Question answered' : (isReading ? 'Stop reading' : 'Read question and options')}
                        />
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {currentQuestion.options.map((option, index) => {
                            const isCorrect = index === currentQuestion.correct;
                            const isSelected = selectedAnswer === index;
                            const showCorrect = showResult && isCorrect;
                            const showIncorrect = showResult && isSelected && !isCorrect;
                            const optionLabel = String.fromCharCode(65 + index); // A, B, C, D
                            
                            return (
                                <div key={index} style={{ position: 'relative' }}>
                                    <Button
                                        size="large"
                                        onClick={() => handleAnswer(index)}
                                        disabled={answered}
                                        style={{
                                            height: '50px',
                                            fontSize: '18px',
                                            textAlign: 'left',
                                            borderRadius: '10px',
                                            border: showCorrect 
                                                ? '2px solid #52c41a' 
                                                : showIncorrect 
                                                ? '2px solid #ff4d4f' 
                                                : '1px solid #d9d9d9',
                                            background: showCorrect
                                                ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)'
                                                : showIncorrect
                                                ? 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)'
                                                : 'white',
                                            color: showCorrect || showIncorrect ? '#000' : '#000',
                                            fontWeight: 600,
                                            paddingRight: '50px',
                                            width: '100%',
                                            fontFamily: 'monospace'
                                        }}
                                        icon={showCorrect ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : showIncorrect ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> : null}
                                    >
                                        <span style={{ fontWeight: 600, marginRight: '8px' }}>{optionLabel}.</span>
                                        {option}
                                    </Button>
                                    <Button
                                        type="text"
                                        shape="circle"
                                        icon={<SoundOutlined style={{ fontSize: '14px' }} />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!answered) {
                                                readOption(option, index);
                                            }
                                        }}
                                        disabled={answered}
                                        style={{
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: answered ? 'rgba(217, 217, 217, 0.3)' : 'rgba(24, 144, 255, 0.1)',
                                            border: 'none',
                                            color: answered ? '#bfbfbf' : '#1890ff',
                                            cursor: answered ? 'not-allowed' : 'pointer',
                                            opacity: answered ? 0.5 : 1
                                        }}
                                        title={answered ? 'Question answered' : `Read option ${optionLabel}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.8;
                    }
                }
            `}</style>
        </div>
    );
};

export default MathQuiz;

